"use client";
import { useEffect, useState } from "react";
import Image from "next/legacy/image";
import { Link } from "components/shared/Link";
import cn from "classnames";
import { Button } from "components/UI/buttons/Button";
import { Container } from "../Container";
import st from "@/styles/modules/typography.module.scss";
import s from "./Header.module.scss";

import { SpriteIcon } from "components/UI/SpriteIcon";
import searchIcon from "public/img/icons/search.svg";
import PhoneIcon from "public/img/icons/phone.svg";
import { BurgerButton } from "components/UI/buttons/BurgerButton";
import { Portal } from "components/shared/Portal";
import { HeaderSearchForm } from "components/shared/HeaderSearchForm";
import { setOpen as setSearchOpen } from "store/samplesSearch/samplesSearchSlice";
import { useAppDispatch, useAppSelector } from "store";
import { freeScroll, lockMobileScroll, lockScroll } from "utils/helpers";
import { PHONE_LINK, PHONE_TEXT, WRITING_SERVICE_SUBDOMAIN } from "@/const";
import { useHeaderMenu } from "./useHeaderMenu";
import { debounce, throttle } from "lodash";
import { HeaderDropdown } from "@/components/layout/header/HeaderDropdown";
import IconArrowRight from "public/img/icons/arrow-right.svg";
import { iconDict } from "@/components/pages/tools/ToolIcons";
import { isWritingServiceSelector } from "@/store/global/globalSlice";
import { usePathname } from "next/navigation";

export const Header = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [transition, setTransition] = useState(false);

  const [openedDropdown, setOpenedDropdown] = useState<string | undefined>();

  const [isFixed, setFixed] = useState(false);

  const [selectedDropdownItem, setSelectedDropdownItem] = useState<string | null>(null);
  const [selectedDropdownSubItem, setSelectedDropdownSubItem] = useState<string | null>(null);

  const menu = useHeaderMenu();

  const pathname = usePathname();

  const dispatch = useAppDispatch();

  const isWritingService = useAppSelector(isWritingServiceSelector);

  const openMenu = () => {
    setTransition(true);
    lockScroll();
    setMenuOpen(true);
    setTimeout(() => {
      setTransition(false);
    }, 500);
  };

  const closeMenu = () => {
    setTransition(true);
    setMenuOpen(false);
    setTimeout(() => {
      setTransition(false);
      setSelectedDropdownItem(null);
      setSelectedDropdownSubItem(null);
      freeScroll();
    }, 500);
  };

  const onBurgerClick = () => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  useEffect(() => {
    const checkFixed = throttle(() => {
      setFixed(window.scrollY > 10);
    }, 200);
    checkFixed();

    window.addEventListener("scroll", checkFixed);

    return () => {
      window.removeEventListener("scroll", checkFixed);
    };
  }, []);

  const currentDropdownItem = menu.find(({ name }) => name === selectedDropdownItem);

  const currentDropdownSubItem = currentDropdownItem?.items?.find(
    ({ text }) => text === selectedDropdownSubItem
  );

  const mobileViewTarget = currentDropdownSubItem || currentDropdownItem;

  return (
    <header
      className={cn(s.header, {
        [s.headerMenuOpen]: isMenuOpen,
        [s.isFixed]: isFixed,
        [s.mobileNestedItemOpen]: !!currentDropdownItem,
        [s.noSubItemSelected]: !!currentDropdownSubItem,
      })}
    >
      <Container className={s.container}>
        <div className={s.headerInner}>
          <div className={s.headerLeft}>
            <div className={s.headerLogo}>
              <Link href="/" includeSubdomain>
                <span className={s.logoDesk}>
                  {" "}
                  <Image src="/img/logo.svg" alt="logo" width={187} height={35} />
                </span>
                <span className={s.logoMob}>
                  <Image src="/img/logo-alt.svg" alt="logo" width={35} height={35} />
                </span>
              </Link>
            </div>

            <div
              className={cn(s.headerNavWrap, {
                [s.headerNavWrapTransition]: transition,
              })}
            >
              <div className={s.headerNav}>
                <div className={s.headerNavInner}>
                  <a href={PHONE_LINK} className={cn(s.headerPhone, s.headerPhoneXs, "link")}>
                    <SpriteIcon icon={PhoneIcon} className={s.headerPhoneIcon} />
                    {PHONE_TEXT}
                  </a>

                  <nav className={s.headerNavMenu}>
                    {mobileViewTarget && (
                      <div className={s.mobileMenuView}>
                        <button
                          className={s.mobileMenuBtn}
                          onClick={() => {
                            if ("name" in mobileViewTarget) {
                              setSelectedDropdownItem(null);
                            } else {
                              setSelectedDropdownSubItem(null);
                            }
                          }}
                        >
                          <SpriteIcon icon={IconArrowRight} />
                          <span>
                            {"icon" in mobileViewTarget && mobileViewTarget.icon && (
                              <SpriteIcon icon={iconDict[mobileViewTarget.icon]} />
                            )}
                            {"name" in mobileViewTarget ? mobileViewTarget.name : mobileViewTarget.text}
                          </span>
                        </button>
                        {!!mobileViewTarget.items?.length && (
                          <div
                            className={cn(s.mobileMenuSubitems, {
                              [s.mobileMenuNestedSubitems]: !("name" in mobileViewTarget),
                            })}
                          >
                            {mobileViewTarget.items.map((item, key) => {
                              if ("items" in item && !!item.items?.length) {
                                return (
                                  <button
                                    className={cn(s.mobileMenuSubitemBtn, {
                                      [s.mobBtnActive]:
                                        pathname === String(item.href) ||
                                        String(item.href) === "/" + pathname.split("/")[1],
                                    })}
                                    onClick={() => setSelectedDropdownSubItem(item.text)}
                                    key={key}
                                  >
                                    <span>
                                      {item.icon && (
                                        <SpriteIcon
                                          icon={iconDict[item.icon]}
                                          className={s.mobileMenuSubitemIcon}
                                        />
                                      )}
                                      {item.text}
                                    </span>
                                    <SpriteIcon icon={IconArrowRight} />
                                  </button>
                                );
                              }

                              return (
                                <Link
                                  href={String(item.href)}
                                  className={cn(s.mobileMenuSubitemBtn, {
                                    [s.mobBtnActive]:
                                      pathname === String(item.href) ||
                                      String(item.href) === "/" + pathname.split("/")[1],
                                  })}
                                  key={key}
                                  onClick={() => {
                                    closeMenu();
                                  }}
                                >
                                  <span>
                                    {"icon" in item && item.icon && (
                                      <SpriteIcon
                                        icon={iconDict[item.icon]}
                                        className={s.mobileMenuSubitemIcon}
                                      />
                                    )}
                                    {item.text}
                                  </span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <ul className={s.headerMenu}>
                      {menu.map((item) => (
                        <li key={item.name} className={s.headerMenuItem}>
                          {item.href && (
                            <Link
                              href={item.href}
                              includeSubdomain={item.includeSubdomain}
                              className={cn(st.link, {
                                [st.linkActive]: pathname === item.href,
                              })}
                            >
                              {item.name}
                            </Link>
                          )}
                          {item.items && (
                            <HeaderDropdown
                              title={item.name}
                              items={item.items || []}
                              currentOpenedDropdown={openedDropdown}
                              onOpen={setOpenedDropdown}
                              onOpenItem={setSelectedDropdownItem}
                              onOpenSubItem={setSelectedDropdownSubItem}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                    <div className={s.headerMobBtns}>
                      <Button
                        tag="dashboardLink"
                        type="secondary"
                        className={cn(s.headerBtn, s.headerLoginBtnSm)}
                        href="/orders"
                      >
                        Dashboard
                      </Button>
                      {isWritingService ? (
                        <Button
                          href="/order"
                          tag="dashboardLink"
                          type="accent"
                          className={cn(s.headerBtn, s.headerHireMdButton)}
                        >
                          Order now
                        </Button>
                      ) : (
                        <Button
                          href="/"
                          tag="subdomainLink"
                          subdomain={WRITING_SERVICE_SUBDOMAIN}
                          type="accent"
                          className={cn(s.headerBtn, s.headerHireMdButton)}
                        >
                          Writing service
                        </Button>
                      )}
                    </div>
                  </nav>
                </div>
              </div>
            </div>
            <a href={PHONE_LINK} className={s.headerPhone}>
              <SpriteIcon icon={PhoneIcon} className={s.headerPhoneIcon} />
              {PHONE_TEXT}
            </a>
          </div>

          <div className={s.headerRight}>
            <Button
              tag="button"
              type="secondary"
              className={cn(s.headerBtn, s.headerBtnSearch)}
              ariaLabel="Search"
              onClick={() => {
                lockMobileScroll();
                dispatch(setSearchOpen(true));
              }}
            >
              <SpriteIcon icon={searchIcon} />
            </Button>
            <Portal>
              <HeaderSearchForm />
            </Portal>
            <Button
              tag="dashboardLink"
              type="secondary"
              className={cn(s.headerBtn, s.headerLoginBtn)}
              href="/orders"
            >
              Dashboard
            </Button>
            {isWritingService ? (
              <Button
                href="/order"
                tag="dashboardLink"
                type="accent"
                className={cn(s.headerBtn, s.headerBtnDesk)}
              >
                Order now
              </Button>
            ) : (
              <Button
                href="/"
                tag="subdomainLink"
                subdomain={WRITING_SERVICE_SUBDOMAIN}
                type="accent"
                className={cn(s.headerBtn, s.headerBtnDesk)}
              >
                Writing service
              </Button>
            )}

            <BurgerButton active={isMenuOpen} onClick={onBurgerClick} />
          </div>
        </div>
      </Container>
    </header>
  );
};
